import React, { useState } from 'react';
import './FeedbackForm.css';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, MenuItem, FormControlLabel, Checkbox } from '@mui/material';

function FeedbackForm({ onNewFeedback, clientId, managers, slug }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [category, setCategory] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // New loading state


  const [otherCategory, setOtherCategory] = useState(""); // For "Other" category input
  const [sentiment, setSentiment] = useState("AI Determined");
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [managerId, setManagerId] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // NEW: Opt-In checkboxes
  const [optInEmail, setOptInEmail] = useState(false);
  const [optInSMS, setOptInSMS] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true); 
  
    // Validate contact fields
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setErrorMessage("All contact fields (Name, Email, and Phone) are required.");
      setIsLoading(false);
      return;
    }
  
    // If user picked "Other" among the categories but didn't specify it
    if (selectedCategories.includes("Other") && !otherCategory.trim()) {
      setErrorMessage("Please specify a category for 'Other'.");
      setIsLoading(false);
      return;
    }
  
    // Replace any "Other" with the user-specified input
    const finalCategories = selectedCategories.map(cat => (cat === "Other" ? otherCategory.trim() : cat));
  
    // Get AI sentiment and title (unchanged)
    const aiSentiment = await determineSentiment(text);
    const aiReviewTitle = await generateTitle(text);
  
    // Prepare the feedback object – note that we send `categories` (an array) instead of `category`
    const newFeedback = {
      text,
      categories: finalCategories, // new multi-select field
      sentiment: aiSentiment,
      rating,
      client_id: clientId,
      name,
      email,
      phone,
      manager_id: managerId
    };
  
    // (Remaining POST logic remains the same)
    try {
      const feedbackResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback.php`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newFeedback),
        }
      );
  
      if (!feedbackResponse.ok) {
        setErrorMessage("Failed to submit feedback.");
        setIsLoading(false);
        return;
      }
  
      const feedbackData = await feedbackResponse.json();
      onNewFeedback(feedbackData);
  
      // Redirect after success
      const encodedFeedback = encodeURIComponent(text);
      const encodedTitle = encodeURIComponent(aiReviewTitle);
      const encodedSentiment = encodeURIComponent(aiSentiment);
  
      navigate(`/client/${slug}/success?sentiment=${encodedSentiment}&feedback=${encodedFeedback}&title=${encodedTitle}&name=${name}`, {
        replace: true,
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setIsLoading(false);
      setErrorMessage("Error submitting feedback. Please try again.");
    }
  };
  

  async function generateTitle(feedbackText) {
    const url = 'https://sentiment-analyze-42ebdff2d1b7.herokuapp.com/generate_title'; 
    // or wherever your Flask is hosted
  
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback: feedbackText }),
      });
  
      if (response.ok) {
        const result = await response.json();
        return result.title; // e.g. "Great Place to Work"
      } else {
        console.error(`Error calling Flask title API: ${response.status} - ${response.statusText}`);
        return 'Untitled'; // fallback
      }
    } catch (error) {
      console.error('Error calling Flask title API:', error);
      return 'Untitled'; // fallback
    }
  }
  
async function determineSentiment(text) {
  const url = 'https://sentiment-analyze-42ebdff2d1b7.herokuapp.com/analyze';
  const data = { text };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      const result = await response.json();
      return result.sentiment;
    } else {
      console.error(`Error calling Flask API: ${response.status} - ${response.statusText}`);
      return 'Neutral'; // Fallback sentiment
    }
  } catch (error) {
    console.error('Error calling Flask API:', error);
    return 'Neutral'; // Fallback sentiment
  }
}

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: "white", // White text for inputs
      marginBottom: '1em',
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "white" },
      "&.Mui-focused fieldset": { borderColor: "white" },
    },
    "& .MuiInputLabel-root": { color: "white" }, // White label text
    "& .MuiSelect-icon": { color: "white" }, // White dropdown icon
  };

  return (
    <form onSubmit={handleSubmit} className="right-content">
      <h2>Feedback Form</h2>
      {submitSuccess && <p className="success-message">Thank you for your feedback!</p>}
      {errorMessage && <p className="error-message">{errorMessage}</p>}

      <TextField
        id="feedback"
        label="Feedback Message:"
        variant="outlined"
        multiline
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="feedback-input"
        sx={commonTextFieldStyles}
      />

      <TextField
        select
        label="Select Inquiry Categories"
        value={selectedCategories}
        onChange={(e) => setSelectedCategories(e.target.value)}
        variant="outlined"
        SelectProps={{ multiple: true }}
        sx={commonTextFieldStyles}
      >
        {["Equipment", "Pay", "Mileage", "Benefits", "Home Time", "People", "Support", "Safety", "Other"].map((cat) => (
          <MenuItem key={cat} value={cat} 
            style={{ 
              color: selectedCategories.includes(cat) ? "white" : "white", 
              backgroundColor: selectedCategories.includes(cat) ? "#02293c" : "grey" 
            }}
          >
            {cat}
          </MenuItem>
        ))}
      </TextField>


      {selectedCategories.includes("Other") && (
        <TextField
          id="other-category"
          label="Specify Category"
          variant="outlined"
          value={otherCategory}
          onChange={(e) => setOtherCategory(e.target.value)}
          sx={commonTextFieldStyles}
        />
      )}


      <TextField
        select
        label="Select Manager"
        value={managerId}
        onChange={(e) => setManagerId(e.target.value)}
        variant="outlined"
        sx={commonTextFieldStyles}
      >
        {managers.map((manager) => (
          <MenuItem
            key={manager.id}
            value={manager.id}
            style={{ color: "white", backgroundColor: "grey" }}
          >
            {manager.name}
          </MenuItem>
        ))}
      </TextField>

      <div className="contact-fields">
        <TextField
          label="Your Name"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={commonTextFieldStyles}
        />
        <TextField
          label="Your Email"
          variant="outlined"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={commonTextFieldStyles}
        />
        <TextField
          label="Your Phone"
          variant="outlined"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          sx={commonTextFieldStyles}
        />
      </div>

      {/* NEW: Opt-In Checkboxes */}
      {/* <div className="optin-checkboxes">
        <FormControlLabel
          control={
            <Checkbox
              checked={optInEmail}
              onChange={(e) => setOptInEmail(e.target.checked)}
              sx={{ color: 'white' }}
            />
          }
          label={<span style={{ color: 'white' }}>Opt in for Email updates</span>}
        />
        
        <p className='sms-description'>I consent to be contacted by or on behalf of National Carriers via email. My information will not be shared. <Link to="/terms">Privacy Policy</Link></p>

        <FormControlLabel
          control={
            <Checkbox
              checked={optInSMS}
              onChange={(e) => setOptInSMS(e.target.checked)}
              sx={{ color: 'white' }}
            />
          }
          label={<span style={{ color: 'white' }}>Opt in for SMS updates</span>}
        />

          <p className='sms-description'>I consent to be contacted by or on behalf of National Carriers via phone call. My information will not be shared. <Link to="/terms">Privacy Policy</Link></p>

          <p className='sms-description'>I consent to be contacted by or on behalf of National Carriers via SMS. Messages will not exceed 2-4 per month. Standard messaging and data rates apply. Text STOP to opt out at any time.</p>

      </div> */}

      <button type="submit" disabled={isLoading} className="submit-btn">
        {isLoading ? <span className="spinner"></span> : "Submit"}
      </button>
      

    </form>
  );
}

export default FeedbackForm;
